import { useCallback } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { setToast } from '../../../redux/mealPlanUISlice';
import { selectCurrentWeekStart } from '../../../redux/mealPlanSlice';
import { useWeekSnapshots } from '../../../hooks/useWeekSnapshots';
import type { RootState } from '../../../redux/store';

export function useUndoableWeek() {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const { snapshot, restore } = useWeekSnapshots();

  /**
   * Run `mutator` and offer an undo that puts the plan back.
   *
   * `alsoWeeks` names any week *other* than the one on screen that the mutation
   * touches — a move to another week, say. Without it the undo would restore
   * the source week (recreating the dish) and leave the copy in the destination
   * week alone, so undoing a cross-week move would duplicate the meal.
   */
  const mutateWithUndo = useCallback((
    message: string,
    mutator: () => void,
    alsoWeeks: string[] = [],
  ) => {
    const currentWeekStart = selectCurrentWeekStart(store.getState());
    const undoKey = snapshot([currentWeekStart, ...alsoWeeks]);
    mutator();
    dispatch(setToast({ text: message, undoKey }));
  }, [dispatch, store, snapshot]);

  return { mutateWithUndo, undo: restore };
}
